import React, { useEffect, useState } from "react"
import { Button, Chip, FormControl, Grid, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@material-ui/core"
import { GetApp as DownloadIcon } from "@material-ui/icons"
import { ptBR } from "date-fns/locale"
import DateFnsUtils from "@date-io/date-fns"
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers"

import {
	Portlet,
	ProductReportClient,
	TypographyListGroup
} from "@/components"
import { ProductReportClientProps } from "@/components/ProductReportClient"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import { TableColumn } from "@/components/DataTable"

import { colors } from "@/styles/colors"

import ColorUtil from "@/utils/ColorUtil"

import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import { formatDateInBrazilianDate, formatDateInHours, safelyCastDate } from "@/utils/time"

import { turnBilledDurationSecondsInCredits } from "@/utils/Voice"

import { reportsTypeToTitle } from "@/protocols/reports"
import { VoiceCallStatus, VoiceCallUsageHistoryEntry } from "@/protocols/usage"
import { UserInInstance } from "@/protocols/userInInstance"

import ErrorHandlerService from "@/services/ErrorHandler"
import UsageService from "@/services/Usage"
import UserInInstanceService from "@/services/UserInInstance"

import useDebounce from "@/hooks/useDebounce"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/Reports/VoiceCallReport/styles"

import VoiceCallReportSkeleton from "@/pages/Admin/Reports/VoiceCallReport/VoiceCallReportSkeleton"

const SECONDS_IN_MINUTE = 60
const DEFAULT_ATTENDANT_LABEL = "-"
const INVALID_DATE_TIME_LABEL = "Data inválida"
const DEFAULT_VALUE_SELECT_ATTENDANT_INPUT = "Todos os atendentes"
const NO_DURATION_LABEL = "-"

type VoiceCallStatusDefinition = {
	label: string
	color: string
}

const voiceCallStatusToDefinition: Record<VoiceCallStatus, VoiceCallStatusDefinition> = {
	answered: {
		label: "Atendida",
		color: colors.green[600]
	},
	busy: {
		label: "Não atendida",
		color: colors.red[600]
	},
	"no-answer": {
		label: "Não atendida",
		color: colors.red[600]
	},
	failed: {
		label: "Não atendida",
		color: colors.red[600]
	},
	cancelled: {
		label: "Não atendida",
		color: colors.red[600]
	}
}

type RecordingDownloadInfo = {
	recordingFileUrl: string | null
	status: VoiceCallStatus
}

export type VoiceCallReportRow = {
	id: number
	dateTimeLabel: React.ReactNode
	contact?: ProductReportClientProps["client"]
	attendantName: string
	durationLabel: string
	status: VoiceCallStatus
	creditsConsumed: number | string
	recordingDownloadInfo: RecordingDownloadInfo
}

export type VoiceCallReportSearchFilters = {
	fromDate: Date | null
	toDate: Date | null
	userId: number | null
	contactSearch: string
}

const formatCallDurationLabel = (totalDurationSeconds: number): string => {
	if (totalDurationSeconds === 0) {
		return NO_DURATION_LABEL
	}

	const minutes = Math.floor(totalDurationSeconds / SECONDS_IN_MINUTE)
	const seconds = totalDurationSeconds % SECONDS_IN_MINUTE
	const formattedSeconds = String(seconds).padStart(2, "0")

	return `${minutes}:${formattedSeconds}`
}

const formatCreditsConsumedLabel = (billedDurationSeconds: number): number | string => {
	if (billedDurationSeconds === 0) {
		return NO_DURATION_LABEL
	}

	return turnBilledDurationSecondsInCredits(billedDurationSeconds)
}

const renderContact = (value: VoiceCallReportRow["contact"]): React.ReactNode => (
	<ProductReportClient client={value} />
)

const renderStatus = (value: VoiceCallReportRow["status"]): React.ReactNode => {
	const statusDefinition = voiceCallStatusToDefinition[value]

	return (
		<Chip
			label={statusDefinition.label}
			style={{
				...ColorUtil.getBackgroundAndContrastColor(statusDefinition.color),
				fontWeight: 600
			}}
		/>
	)
}

const renderRecordingDownload = (value: VoiceCallReportRow["recordingDownloadInfo"]): React.ReactNode => {
	const isAnswered = value.status === "answered"
	const hasRecordingUrl = !!value.recordingFileUrl

	if (!isAnswered || !hasRecordingUrl) {
		return NO_DURATION_LABEL
	}

	return (
		<Tooltip title="Baixar gravação">
			<IconButton
				component="a"
				href={value.recordingFileUrl}
				download
				target="_blank"
				rel="noopener noreferrer"
				size="small"
			>
				<DownloadIcon />
			</IconButton>
		</Tooltip>
	)
}

const voiceCallReportTableColumns: TableColumn<VoiceCallReportRow>[] = [
	{ field: "dateTimeLabel", label: "Data/Hora" },
	{ field: "contact", label: "Contato", rowRender: (value) => renderContact(value as VoiceCallReportRow["contact"]) },
	{ field: "attendantName", label: "Atendente" },
	{ field: "durationLabel", label: "Duração" },
	{ field: "status", label: "Status", rowRender: (value) => renderStatus(value as VoiceCallReportRow["status"]) },
	{ field: "creditsConsumed", label: "Créditos consumidos" },
	{ field: "recordingDownloadInfo", label: "Gravação", align: "center", rowRender: (value) => renderRecordingDownload(value as VoiceCallReportRow["recordingDownloadInfo"]) }
]

const VoiceCallReport: React.FC = () => {
	const classes = useStyles()
	const customClasses = useCustomStyles()

	const [rows, setRows] = useState<VoiceCallReportRow[]>([])
	const [totalRows, setTotalRows] = useState(0)
	const [loading, setLoading] = useState(true)
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [attendants, setAttendants] = useState<UserInInstance[]>([])
	const [searchFilters, setSearchFilters] = useState<VoiceCallReportSearchFilters>({
		fromDate: null,
		toDate: null,
		userId: null,
		contactSearch: ""
	})

	const getDateTimeLabel = (createdAt: string): React.ReactNode => {
		const createdDate = safelyCastDate(createdAt)
		const createdDateIsValid = createdDate instanceof Date

		if (!createdDateIsValid) {
			return (
				<Typography className={customClasses.italicText}>
					{INVALID_DATE_TIME_LABEL}
				</Typography>
			)
		}

		return (
			<TypographyListGroup>
				<Typography>
					{formatDateInBrazilianDate(createdDate)}
				</Typography>

				<Typography className={customClasses.italicText}>
					{formatDateInHours(createdDate)}
				</Typography>
			</TypographyListGroup>
		)
	}

	const formatHistoryEntry = (entry: VoiceCallUsageHistoryEntry): VoiceCallReportRow => {
		const contact: VoiceCallReportRow["contact"] = entry.contactNickname || entry.contactName || entry.contactPhoneNumber ? {
			id: entry.clientId as number,
			nickname: entry.contactNickname,
			name: entry.contactName,
			phoneNumber: entry.contactPhoneNumber
		} : undefined

		return {
			id: entry.id,
			dateTimeLabel: getDateTimeLabel(entry.createdAt),
			contact,
			attendantName: entry.attendantName ?? DEFAULT_ATTENDANT_LABEL,
			durationLabel: formatCallDurationLabel(entry.data.billedDurationInSeconds),
			status: entry.data.status,
			creditsConsumed: formatCreditsConsumedLabel(entry.data.billedDurationInSeconds),
			recordingDownloadInfo: {
				recordingFileUrl: entry.data.recordingFileUrl ?? null,
				status: entry.data.status
			}
		}
	}

	const loadHistory = async () => {
		setLoading(true)

		try {
			const response = await UsageService.getUsageHistory({
				type: "voice-call",
				page: paginationState.page + 1,
				perPage: paginationState.rowsPerPage,
				userId: searchFilters.userId ?? undefined,
				createdAtFrom: searchFilters.fromDate ? searchFilters.fromDate.toISOString() : undefined,
				createdAtTo: searchFilters.toDate ? searchFilters.toDate.toISOString() : undefined,
				contactSearch: searchFilters.contactSearch || undefined
			})

			const formattedRows = response.data.map(formatHistoryEntry)

			setRows(formattedRows)
			setTotalRows(response.pagination.total)
		} catch (error) {
			ErrorHandlerService.handle(error as Error)
		}

		setLoading(false)
	}

	const loadAttendants = async () => {
		try {
			const response = await UserInInstanceService.getUsersInInstance({
				status: ["active"]
			})

			if (response) {
				setAttendants(response)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as Error)
		}
	}

	const handlePaginationChange = (newPaginationState: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...newPaginationState
		}))
	}

	const handleChangeEditFilters = (data: Partial<VoiceCallReportSearchFilters>) => {
		setSearchFilters({
			...searchFilters,
			...data
		})
	}

	const handleApplyFilters = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		setPaginationState(lastState => ({
			...lastState,
			page: DEFAULT_WHERE_DATA.page
		}))
	}

	useEffect(() => {
		loadHistory()
		// eslint-disable-next-line
	}, [paginationState.page, paginationState.rowsPerPage, searchFilters.fromDate, searchFilters.toDate, searchFilters.userId])

	useDebounce(
		async () => {
			loadHistory()
		},
		searchFilters.contactSearch,
		2250
	)

	useEffect(() => {
		loadAttendants()
	}, [])

	return (
		<Grid
			container
			direction="column"
			spacing={6}
		>
			<Grid item>
				<Typography
					variant="h5"
					className={classes.titleText}
				>
					{reportsTypeToTitle["voice-call-report"]}
				</Typography>
			</Grid>

			<Grid item>
				<Portlet elevation={1}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<form onSubmit={handleApplyFilters}>
								<Grid container justifyContent="space-between" spacing={1} xs={12}>
									<Grid item xs={11}>
										<Grid container spacing={1}>
											<Grid item xs={3}>
												<FormControl fullWidth>
													<Select
														value={searchFilters.userId ?? DEFAULT_VALUE_SELECT_ATTENDANT_INPUT}
														variant="outlined"
														fullWidth
														onChange={
															({ target }) => {
																const selectedValue = target.value as number | string
																const userId = selectedValue === DEFAULT_VALUE_SELECT_ATTENDANT_INPUT ? null : selectedValue
																handleChangeEditFilters({ userId: userId as number | null })
															}
														}
													>
														<MenuItem value={DEFAULT_VALUE_SELECT_ATTENDANT_INPUT}>Todos os atendentes</MenuItem>
														{attendants.map((attendant) => (
															<MenuItem key={attendant.id} value={attendant.id}>{attendant.name}</MenuItem>
														))}
													</Select>
												</FormControl>
											</Grid>
											<Grid item xs={3}>
												<MuiPickersUtilsProvider
													locale={ptBR}
													utils={DateFnsUtils}
												>
													<KeyboardDatePicker
														okLabel="Pronto"
														cancelLabel="Cancelar"
														value={searchFilters.fromDate}
														variant="inline"
														inputVariant="outlined"
														onChange={(date) => handleChangeEditFilters({ fromDate: date })}
														placeholder="De"
														size="small"
														format="dd/MM/yyyy"
														InputAdornmentProps={{
															position: "start"
														}}
														InputProps={{
															classes: { root: classes.dateInput }
														}}
													/>
												</MuiPickersUtilsProvider>
											</Grid>
											<Grid item xs={3}>
												<MuiPickersUtilsProvider
													locale={ptBR}
													utils={DateFnsUtils}
												>
													<KeyboardDatePicker
														okLabel="Pronto"
														cancelLabel="Cancelar"
														value={searchFilters.toDate}
														variant="inline"
														inputVariant="outlined"
														onChange={(date) => handleChangeEditFilters({ toDate: date })}
														placeholder="Até"
														size="small"
														format="dd/MM/yyyy"
														InputAdornmentProps={{
															position: "start"
														}}
														InputProps={{
															classes: { root: classes.dateInput }
														}}
													/>
												</MuiPickersUtilsProvider>
											</Grid>
											<Grid item xs={3}>
												<FormControl fullWidth>
													<TextField
														value={searchFilters.contactSearch}
														onChange={
															({ target }) => handleChangeEditFilters({ contactSearch: target.value?.trimStart() })
														}
														placeholder="Buscar contato"
														fullWidth
														variant="outlined"
													/>
												</FormControl>
											</Grid>
										</Grid>
									</Grid>
									<Grid item xs={1}>
										<Button
											variant="contained"
											color="primary"
											type="submit"
											className={classes.searchButton}
										>
											Buscar
										</Button>
									</Grid>
								</Grid>
							</form>
						</Grid>

						{loading ? (
							<Grid item xs={12}>
								<VoiceCallReportSkeleton columnList={voiceCallReportTableColumns} />
							</Grid>
						) : (
							<Grid item xs={12}>
								<DataTableWithPagination
									columns={voiceCallReportTableColumns}
									data={rows}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={totalRows}
									onPaginationChange={handlePaginationChange}
								/>
							</Grid>
						)}
					</Grid>
				</Portlet>
			</Grid>
		</Grid>
	)
}

export default VoiceCallReport
